import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Save, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  type Client,
  type ClientNote,
  type ClientRollup,
  addClientNote,
  createClient,
  listClientNotes,
  listClients,
  loadClientRollup,
} from "@/lib/clients";

export const Route = createFileRoute("/tools/clients")({
  head: () => ({
    meta: [{ title: "Customers" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: ClientsPage,
});

function ClientsPage() {
  const [list, setList] = useState<Client[]>([]);
  const [search, setSearch] = useState("");
  const [current, setCurrent] = useState<Client | null>(null);

  const load = () => listClients().then(setList).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const addNew = async () => {
    const name = prompt("Customer name");
    if (!name?.trim()) return;
    try {
      const c = await createClient({ name });
      load();
      setCurrent(c);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (current) {
    return <ClientDetail client={current} onBack={() => { setCurrent(null); load(); }} />;
  }

  const q = search.trim().toLowerCase();
  const filtered = q
    ? list.filter((c) =>
        [c.name, c.company, c.email, c.phone, c.city].some((v) => v?.toLowerCase().includes(q)),
      )
    : list;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            One shared record per customer. Pick them in any tool to auto-fill contact info.
          </p>
        </div>
        <Button onClick={addNew}><Plus className="mr-1 h-4 w-4" /> New customer</Button>
      </div>
      <Input
        className="mt-4 max-w-sm"
        placeholder="Search customers…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((c) => (
          <button
            key={c.id}
            onClick={() => setCurrent(c)}
            className="rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-accent"
          >
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold">{c.name}</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {[c.phone, c.email].filter(Boolean).join(" · ") || "No contact info"}
            </p>
            <p className="text-xs text-muted-foreground">
              {[c.address, c.city].filter(Boolean).join(", ")}
            </p>
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground">No customers yet.</p>
        )}
      </div>
    </div>
  );
}

function ClientDetail({ client, onBack }: { client: Client; onBack: () => void }) {
  const [form, setForm] = useState<Client>(client);
  const [rollup, setRollup] = useState<ClientRollup | null>(null);
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    loadClientRollup(client).then(setRollup).catch(() => {});
    listClientNotes(client.id).then(setNotes).catch(() => {});
  }, [client]);

  const save = async () => {
    const { error } = await supabase
      .from("clients")
      .update({
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        address: form.address,
        city: form.city,
        state: form.state,
        postal_code: form.postal_code,
        notes: form.notes,
      })
      .eq("id", client.id);
    if (error) return toast.error(error.message);
    toast.success("Saved");
  };

  const del = async () => {
    if (!confirm("Delete this customer? Linked records stay but lose the link.")) return;
    const { error } = await supabase.from("clients").delete().eq("id", client.id);
    if (error) return toast.error(error.message);
    onBack();
  };

  const postNote = async () => {
    if (!note.trim()) return;
    try {
      await addClientNote(client.id, note.trim());
      setNote("");
      setNotes(await listClientNotes(client.id));
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={del}><Trash2 className="mr-1 h-4 w-4" /> Delete</Button>
          <Button onClick={save}><Save className="mr-1 h-4 w-4" /> Save</Button>
        </div>
      </div>

      <div className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div>
          <h1 className="font-display text-2xl font-bold">{form.name}</h1>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Company"><Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
            <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Address"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="City"><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="State"><Input value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
            <Field label="Postal code"><Input value={form.postal_code || ""} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></Field>
          </div>
          <Field label="Customer notes" className="mt-3">
            <Textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>

          {rollup && (
            <div className="mt-8 space-y-6">
              <Group title="Projects" to="/tools/projects" empty="No projects yet.">
                {rollup.projects.map((p) => (
                  <Row key={p.id} left={p.title} right={p.status.replace("_", " ")} />
                ))}
              </Group>
              <Group title="Saved estimates" to="/tools/estimates" empty="No estimates yet.">
                {rollup.estimates.map((e) => (
                  <Row key={e.id} left={e.name} right={`$${Number(e.total).toLocaleString()}`} />
                ))}
              </Group>
              <Group title="Proposals" to="/tools/proposals" empty="No proposals yet.">
                {rollup.proposals.map((p) => (
                  <Row key={p.id} left={p.title} right={`${p.status} · $${Number(p.total).toLocaleString()}`} />
                ))}
              </Group>
              <Group title="Service tickets" to="/tools/service-tickets" empty="No tickets yet.">
                {rollup.tickets.map((t) => (
                  <Row key={t.id} left={`#${t.ticket_number} ${t.issue}`} right={t.status.replace("_", " ")} />
                ))}
              </Group>
              <div>
                <SectionHead title="Job photos" to="/tools/photos" />
                {rollup.photos.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No photos yet.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {rollup.photos.map((p) => (
                      <img
                        key={p.id}
                        src={p.url}
                        alt={p.caption || "Job site photo"}
                        loading="lazy"
                        className="aspect-square w-full rounded-md border border-border object-cover"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <aside>
          <h2 className="font-semibold">Activity notes</h2>
          <Textarea
            className="mt-2"
            rows={3}
            placeholder="Add a note visible from every tool…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button size="sm" className="mt-2" onClick={postNote}>Add note</Button>
          <div className="mt-4 space-y-2">
            {notes.map((n) => (
              <div key={n.id} className="rounded-md border border-border bg-card p-3">
                <p className="text-sm whitespace-pre-wrap">{n.body}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(n.created_at).toLocaleString("en-US", { timeZone: "UTC" })}
                </p>
              </div>
            ))}
            {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>
      {children}
    </label>
  );
}

function SectionHead({ title, to }: { title: string; to: string }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <h2 className="font-semibold">{title}</h2>
      <Link to={to} className="text-xs text-muted-foreground underline">Open tool</Link>
    </div>
  );
}

function Group({ title, to, empty, children }: { title: string; to: string; empty: string; children: React.ReactNode }) {
  const items = Array.isArray(children) ? children : [children];
  const hasItems = items.flat().filter(Boolean).length > 0;
  return (
    <div>
      <SectionHead title={title} to={to} />
      {hasItems ? (
        <div className="divide-y divide-border rounded-md border border-border">{children}</div>
      ) : (
        <p className="text-sm text-muted-foreground">{empty}</p>
      )}
    </div>
  );
}

function Row({ left, right }: { left: string; right: string }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 text-sm">
      <span className="truncate">{left}</span>
      <span className="shrink-0 text-xs uppercase text-muted-foreground">{right}</span>
    </div>
  );
}
